/**
 * "What is worked, and how regularly" — one row per lead source Kate
 * registered. Presentational; a row click opens the source drawer.
 */
import { Link } from "react-router-dom";

import type { LeadSource } from "../../api/leadSources";
import { formatRelative } from "../../lib/formatRelative";
import { blockedSentence, reviewTaskLabel, sourceStatusPill } from "../../lib/leadSources";
import { Pill } from "../Pill";

export function SourcesTable({
  sources,
  onOpen,
}: {
  sources: LeadSource[];
  onOpen: (slug: string) => void;
}) {
  return (
    <div className="panel panel--flush overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Kind</th>
            <th>Cadence</th>
            <th>Status</th>
            <th>Last worked</th>
            <th>Last pass</th>
            <th className="num">Proposed</th>
            <th className="num">Interesting</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => {
            const pill = sourceStatusPill(s);
            return (
              <tr key={s.id} className="cursor-pointer" onClick={() => onOpen(s.slug)}>
                <td>
                  <div className="font-heading font-semibold text-ink">{s.name}</div>
                  {s.url ? (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-ink-low"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {s.slug} ↗
                    </a>
                  ) : (
                    <span className="text-xs text-ink-low">{s.slug}</span>
                  )}
                </td>
                <td className="text-sm text-ink-medium">{s.kind}</td>
                <td className="text-sm text-ink-medium">{s.cadence}</td>
                <td className="min-w-[180px] max-w-[280px]">
                  <Pill value={pill.label} tone={pill.tone} />
                  {s.status === "blocked" ? (
                    <div className="text-xs mt-1 text-danger">
                      {blockedSentence(s.blocked_reason)}
                      <span className="text-ink-low">
                        {" · "}
                        {s.manual_review_task_id ? (
                          <Link
                            className="link"
                            to={`/tasks?task=${s.manual_review_task_id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {reviewTaskLabel(s)}
                          </Link>
                        ) : (
                          reviewTaskLabel(s)
                        )}
                      </span>
                    </div>
                  ) : null}
                </td>
                <td className="text-sm tnum whitespace-nowrap">
                  <span
                    className={s.overdue ? "font-semibold" : "text-ink-medium"}
                    style={s.overdue ? { color: "var(--c-yellow-dark)" } : undefined}
                  >
                    {s.last_worked_at ? formatRelative(s.last_worked_at) : "never"}
                  </span>
                </td>
                <td className="text-sm text-ink-low max-w-[360px]">{s.last_log?.summary ?? "—"}</td>
                <td className="num">{s.counts.proposed}</td>
                <td className="num">{s.counts.interesting}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
